<script setup lang="ts">
import { onMounted, watch } from "vue";

const emits = defineEmits(["payment-method-changed", "close"]);

const { paymentMethods, getPaymentMethods } = useCheckout();
const { refreshCart } = useCart();
const { selectedPaymentMethod: paymentMethod, setPaymentMethod } =
  useSessionContext();

const isLoading = ref(false);
const pendingPaymentMethodId = ref<string>("");

onMounted(async () => {
  isLoading.value = true;
  await getPaymentMethods();
  if (paymentMethod.value) {
    pendingPaymentMethodId.value = paymentMethod.value.id;
  }
  isLoading.value = false;
});

// Keep local selection in sync if payment method changes elsewhere
watch(paymentMethod, (newVal) => {
  if (newVal?.id) pendingPaymentMethodId.value = newVal.id;
});

const isSubmitLoading = ref(false);
const confirmChangePaymentMethod = async () => {
  if (
    pendingPaymentMethodId.value &&
    pendingPaymentMethodId.value !== paymentMethod.value?.id
  ) {
    isSubmitLoading.value = true;
    try {
      await setPaymentMethod({ id: pendingPaymentMethodId.value });
      // Refresh cart so backend rule-based pseudo products update
      await refreshCart();
      emits("payment-method-changed", pendingPaymentMethodId.value);
    } finally {
      isSubmitLoading.value = false;
    }
  }
};
</script>

<template>
  <div class="mb-10">
    <h3
      class="font-medium text-grey-700 text-lg 3xl:text-xl leading-tight line-clamp-1"
    >
      {{ $t("checkout.paymentMethodLabel") }}
    </h3>
    <div class="mt-5 flex flex-col">
      <div v-if="isLoading || !paymentMethods.length" class="w-full">
        <div class="flex animate-pulse flex-col space-y-4">
          <div class="flex items-center space-x-4 p-4 rounded-md">
            <div class="w-4 bg-light-grey h-4 rounded-full" />
            <div class="flex flex-col space-y-2 flex-1">
              <div class="w-48 bg-light-grey h-5 rounded-md" />
              <div class="w-32 bg-light-grey h-4 rounded-md" />
            </div>
          </div>
          <div class="flex items-center space-x-4 p-4 rounded-md">
            <div class="w-4 bg-light-grey h-4 rounded-full" />
            <div class="flex flex-col space-y-2 flex-1">
              <div class="w-40 bg-light-grey h-5 rounded-md" />
              <div class="w-28 bg-light-grey h-4 rounded-md" />
            </div>
          </div>
        </div>
      </div>
      <template v-else>
        <label
          v-for="singlePaymentMethod in paymentMethods"
          :key="singlePaymentMethod.id"
          class="flex items-start gap-3 cursor-pointer select-none mb-4"
          :class="
            singlePaymentMethod.id === pendingPaymentMethodId
              ? 'order-first'
              : 'order-0'
          "
        >
          <input
            :id="singlePaymentMethod.id"
            v-model="pendingPaymentMethodId"
            :value="singlePaymentMethod.id"
            name="payment-method"
            type="radio"
            class="mt-1"
            :disabled="isSubmitLoading"
            :data-testid="`checkout-payment-method-${singlePaymentMethod.id}`"
            @change="confirmChangePaymentMethod"
          />
          <div class="space-y-1">
            <div class="font-semibold">
              <span>{{ singlePaymentMethod.translated.name }}</span>
            </div>
            <p
              class="text-sm text-grey-700 mt-1"
              v-if="singlePaymentMethod.translated.description"
            >
              {{ singlePaymentMethod.translated.description }}
            </p>
            <div v-if="singlePaymentMethod.media?.url" class="mt-2">
              <img
                loading="lazy"
                :src="singlePaymentMethod.media.url"
                :alt="`Logo of ${singlePaymentMethod.shortName}`"
                class="max-h-12 max-w-32 object-contain"
              />
            </div>
          </div>
        </label>
        <label class="flex items-start gap-8 cursor-pointer select-none mb-4">
          <div></div>
          <div>
            <div class="text-sm text-grey-700 !mt-2 cursor-text">
              <span>{{ $t("checkout.paymentInfoTitle") }} {{ $t("checkout.paymentInfo") }}</span>
            </div>
          </div>
        </label>
      </template>
    </div>
  </div>
</template>
